/**
 * Country Localization Banner
 *
 * Case 1 (unsupported): browser language is NOT in SUPPORTED_LANGUAGES
 *   → Show banner in English with a language selector + Continue
 *
 * Case 2 (mismatch): browser language IS supported but ≠ current page language
 *   → Show banner in browser language recommending to switch
 *
 * Case 3 (match): browser language == current page language
 *   → Do nothing
 */

// Supported theme languages (browser lang prefix → theme locale code)
const SUPPORTED_LANGUAGES = {
  es: 'es',
  en: 'en',
  fr: 'fr',
  it: 'it',
  pl: 'pl',
  pt: 'pt-PT',
  ca: 'ca',
  de: 'de',
};

// Language display names (used in banner text)
const LANGUAGE_NAMES = {
  es: 'Español',
  en: 'English',
  fr: 'Français',
  it: 'Italiano',
  pl: 'Polski',
  'pt-PT': 'Português',
  ca: 'Català',
  de: 'Deutsch',
};

// Mismatch banner translations (keyed by detected browser language)
const MISMATCH_TRANSLATIONS = {
  es: {
    text: 'Parece que prefieres navegar en Español.',
    switch_btn: 'Cambiar a Español',
    stay_btn: 'Continuar',
  },
  en: {
    text: 'It looks like you prefer browsing in English.',
    switch_btn: 'Switch to English',
    stay_btn: 'Continue',
  },
  fr: {
    text: 'Il semble que vous préférez naviguer en Français.',
    switch_btn: 'Passer au Français',
    stay_btn: 'Continuer',
  },
  it: {
    text: 'Sembra che preferisci navigare in Italiano.',
    switch_btn: 'Passa all\'Italiano',
    stay_btn: 'Continua',
  },
  pl: {
    text: 'Wygląda na to, że wolisz przeglądać po Polsku.',
    switch_btn: 'Przełącz na Polski',
    stay_btn: 'Kontynuuj',
  },
  'pt-PT': {
    text: 'Parece que prefere navegar em Português.',
    switch_btn: 'Mudar para Português',
    stay_btn: 'Continuar',
  },
  ca: {
    text: 'Sembla que prefereixes navegar en Català.',
    switch_btn: 'Canviar a Català',
    stay_btn: 'Continuar',
  },
  de: {
    text: 'Anscheinend möchtest du lieber auf Deutsch surfen.',
    switch_btn: 'Auf Deutsch wechseln',
    stay_btn: 'Weiter',
  },
};

class CountryLocalizationBanner {
  constructor() {
    this.container = document.getElementById('CountryLocalizationPopup');
    if (!this.container) return;

    this.currentLanguage = (this.container.dataset.currentLanguage || '').toLowerCase();
    this.testMode = this.container.dataset.testMode === 'true';

    this.init();
  }

  getBrowserLanguage() {
    const browserLang = (navigator.language || 'en').toLowerCase();
    return browserLang.split('-')[0] || 'en';
  }

  init() {
    const browserLangPrefix = this.getBrowserLanguage();
    const themeLocale = SUPPORTED_LANGUAGES[browserLangPrefix] || null;
    const isSupported = themeLocale !== null;
    // currentLanguage is lower-cased, so compare case-insensitively
    // (otherwise pt-PT never matches and Portuguese visitors always see it).
    const isMatch = isSupported && themeLocale.toLowerCase() === this.currentLanguage;

    // Case 3: perfect match — do nothing
    if (isMatch && !this.testMode) return;

    // Check localStorage
    const storageKey = 'country-locale-banner';
    const saved = localStorage.getItem(storageKey);
    const preferenceKey = `${browserLangPrefix}:${this.currentLanguage}`;

    if (saved === preferenceKey && !this.testMode) return;

    if (!isSupported || this.testMode) {
      // Case 1: unsupported language
      this.showUnsupported(storageKey, preferenceKey);
    } else {
      // Case 2: supported but mismatch
      this.showMismatch(themeLocale, storageKey, preferenceKey);
    }
  }

  showUnsupported(storageKey, preferenceKey) {
    const view = this.container.querySelector('[data-view="unsupported"]');
    view.classList.remove('hidden');
    this.container.classList.remove('hidden');
    this.container.setAttribute('aria-hidden', 'false');

    // Dropdown toggle
    const disclosureBtn = view.querySelector('.disclosure__button');
    const dropdownWrapper = view.querySelector('.disclosure__list-wrapper');

    if (disclosureBtn && dropdownWrapper) {
      disclosureBtn.addEventListener('click', () => {
        const isExpanded = disclosureBtn.getAttribute('aria-expanded') === 'true';
        disclosureBtn.setAttribute('aria-expanded', !isExpanded);
        dropdownWrapper.classList.toggle('hidden');
      });
    }

    // Dropdown item selection
    view.querySelectorAll('.disclosure__link').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const value = e.currentTarget.dataset.value;
        const label = view.querySelector('.country-localization-banner__select-label');
        const input = view.querySelector('input[name="locale_code"]');

        if (label) label.textContent = e.currentTarget.textContent.trim();
        if (input) input.value = value;

        disclosureBtn.setAttribute('aria-expanded', 'false');
        dropdownWrapper.classList.add('hidden');
      });
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.disclosure')) {
        if (disclosureBtn) disclosureBtn.setAttribute('aria-expanded', 'false');
        if (dropdownWrapper) dropdownWrapper.classList.add('hidden');
      }
    });

    // Continue submits the localization form and reloads; the browser
    // language stays unsupported, so persist the choice first or the
    // banner reopens on every load. Key uses the DESTINATION locale so
    // the saved preference matches the page after the switch.
    const form = view.querySelector('form');
    if (form) {
      form.addEventListener('submit', () => {
        const input = view.querySelector('input[name="locale_code"]');
        const chosen = input && input.value ? input.value : this.currentLanguage;
        const browserPrefix = preferenceKey.split(':')[0];
        localStorage.setItem(storageKey, browserPrefix + ':' + chosen);
      });
    }

    // Close banner
    this.bindClose(view, storageKey, preferenceKey);
  }

  showMismatch(themeLocale, storageKey, preferenceKey) {
    const view = this.container.querySelector('[data-view="mismatch"]');
    const translations = MISMATCH_TRANSLATIONS[themeLocale] || MISMATCH_TRANSLATIONS['en'];

    // Fill in dynamic content
    const textEl = view.querySelector('[data-mismatch-text]');
    const switchBtn = view.querySelector('[data-mismatch-switch]');
    const stayBtn = view.querySelector('[data-mismatch-stay]');
    const localeInput = view.querySelector('[data-mismatch-locale]');

    if (textEl) textEl.textContent = translations.text;
    if (switchBtn) switchBtn.textContent = translations.switch_btn;
    if (stayBtn) stayBtn.textContent = translations.stay_btn;
    if (localeInput) localeInput.value = themeLocale;

    view.classList.remove('hidden');
    this.container.classList.remove('hidden');
    this.container.setAttribute('aria-hidden', 'false');

    // Stay = dismiss
    if (stayBtn) {
      stayBtn.addEventListener('click', () => {
        localStorage.setItem(storageKey, preferenceKey);
        this.hide();
      });
    }

    // Close banner
    this.bindClose(view, storageKey, preferenceKey);
  }

  bindClose(view, storageKey, preferenceKey) {
    view.querySelectorAll('.country-localization-banner__close').forEach(btn => {
      btn.addEventListener('click', () => {
        localStorage.setItem(storageKey, preferenceKey);
        this.hide();
      });
    });
  }

  hide() {
    this.container.classList.add('hidden');
    this.container.setAttribute('aria-hidden', 'true');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new CountryLocalizationBanner();
});
